import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getDealDeadlines, type DealDeadline } from '../api/home';
import { formatDate } from '../utils/dates';

/**
 * Key dates strip — a compact, type-grouped glance at every close /
 * inspection / appraisal / financing date landing in the next 30 days.
 *
 * Distinct from DealDeadlinesCard (the per-deal table further down): this
 * sits near the top of Home and answers "what's coming up soon?" in one
 * scan. Reuses the same ['deal-deadlines'] query so it dedupes with the
 * table's fetch rather than hitting the endpoint twice.
 *
 * Self-hides (returns null) while loading or when nothing lands in the
 * window, so the dashboard stays clean on light days.
 */

// Deep-red accent, used ONLY for items due within 3 days.
const ACCENT_SOON = '#A32D2D';

const WINDOW_DAYS = 30;
const SOON_DAYS = 3;
const PER_GROUP_CAP = 4;
const SECONDS_PER_DAY = 86400;

interface GroupDef {
  key: string;
  label: string;
  field: 'close_date' | 'inspection_deadline' | 'appraisal_deadline' | 'financing_deadline';
}

const GROUPS: GroupDef[] = [
  { key: 'closing', label: 'Closing', field: 'close_date' },
  { key: 'inspection', label: 'Inspection', field: 'inspection_deadline' },
  { key: 'appraisal', label: 'Appraisal', field: 'appraisal_deadline' },
  { key: 'financing', label: 'Financing', field: 'financing_deadline' },
];

interface StripItem {
  id: number;
  address: string;
  date: number;
  daysUntil: number;
}

interface StripGroup {
  key: string;
  label: string;
  items: StripItem[];
}

/** Days from now (local time) to the given unix-seconds date, rounded up. */
function daysUntil(dateSec: number, nowMs: number): number {
  return Math.ceil((dateSec * 1000 - nowMs) / (SECONDS_PER_DAY * 1000));
}

function relativeLabel(days: number): string {
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days}d`;
}

function shortAddress(deal: DealDeadline): string {
  return deal.address_street || deal.primary_party_names || `Deal #${deal.id}`;
}

export default function KeyDatesStrip() {
  const { data, isLoading } = useQuery({
    queryKey: ['deal-deadlines'],
    queryFn: getDealDeadlines,
    staleTime: 30_000,
  });

  const deals = data?.deals ?? [];

  const groups = useMemo<StripGroup[]>(() => {
    const nowMs = Date.now();
    // Start of today in local time — items strictly before this are past.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const windowStartSec = startOfToday.getTime() / 1000;
    const windowEndSec = windowStartSec + WINDOW_DAYS * SECONDS_PER_DAY;

    return GROUPS.map((g) => {
      const items: StripItem[] = [];
      for (const deal of deals) {
        const date = deal[g.field];
        if (date == null) continue;
        if (date < windowStartSec || date >= windowEndSec) continue;
        items.push({
          id: deal.id,
          address: shortAddress(deal),
          date,
          daysUntil: daysUntil(date, nowMs),
        });
      }
      items.sort((a, b) => a.date - b.date);
      return { key: g.key, label: g.label, items };
    });
  }, [deals]);

  const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);

  // Self-hide while loading or when nothing lands in the window.
  if (isLoading || totalItems === 0) return null;

  return (
    <section style={cardShell}>
      <div style={{ marginBottom: 12 }}>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--text-heading)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          Key dates · next 30 days
        </h2>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 16,
        }}
      >
        {groups.map((g) => (
          <GroupColumn key={g.key} group={g} />
        ))}
      </div>
    </section>
  );
}

function GroupColumn({ group }: { group: StripGroup }) {
  const shown = group.items.slice(0, PER_GROUP_CAP);
  const overflow = group.items.length - shown.length;

  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 6,
          paddingBottom: 6,
          borderBottom: '1px solid var(--border)',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          {group.label}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--text-heading)',
            lineHeight: 1,
          }}
        >
          {group.items.length}
        </span>
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>None</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map((item) => (
            <ItemRow key={`${group.key}-${item.id}`} item={item} />
          ))}
          {overflow > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{overflow} more</div>
          )}
        </div>
      )}
    </div>
  );
}

function ItemRow({ item }: { item: StripItem }) {
  const isSoon = item.daysUntil <= SOON_DAYS;
  const dateColor = isSoon ? ACCENT_SOON : 'var(--text-muted)';
  const dateWeight = isSoon ? 600 : 400;

  return (
    <Link
      to={`/transactions/${item.id}`}
      style={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        minWidth: 0,
        // Small vertical padding so each row clears a ~40px thumb target
        // on a phone. Barely perceptible on desktop.
        padding: '3px 0',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--text-body)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={item.address}
      >
        {item.address}
      </div>
      <div style={{ fontSize: 11, color: dateColor, fontWeight: dateWeight, marginTop: 1 }}>
        {formatDate(item.date)} · {relativeLabel(item.daysUntil)}
      </div>
    </Link>
  );
}

const cardShell = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '18px 20px',
  marginBottom: 28,
} as const;
